// MIDAS V3 — <LiveRefV3> + <ValV3>
//
// Render layer for the bot's PLC live-data tag:
//   <live-ref address="I 34.0" value="TRUE">I 34.0</live-ref>
//
// Source: IMPLEMENTATION_TEMPLATES/ToDo/MidasChat/ref_links_live/machineid/
//         handoff_live_ref/live-ref.tsx (designer handoff, 2026-05-25).
//
// Adaptations applied for V3 integration (per LIVE_REF_INTEGRATION_SPEC.md):
//   • Renamed exports to <LiveRefV3> / <ValV3> to match V3 naming.
//   • anim defaults to "glow" — LOCKED app-wide. Do NOT pass other values
//     at any call site. Reduced-motion path stays the only deviation
//     (CSS handles it via @media (prefers-reduced-motion: reduce)).
//   • onClick is a no-op / console.debug placeholder in v1 — left-click
//     artifact-pane deep-link is hard-deferred to the follow-up ticket
//     `MIDAS-LIVE-REF-INTERACTIONS`. Component contract still accepts
//     onClick so the follow-up can swap in the real handler without
//     changing parser plumbing.
//   • Hover tooltip wraps the chip via the Radix shadcn primitives at
//     `../v2/ui/tooltip` (explicitly reused by V3 per the file header).
//     `TooltipProvider` is mounted ONCE at the V3 layout root
//     (MidasV3Layout.tsx:325, MIDAS-V3-P6 hotfix comment) — we do NOT
//     mount our own here.
//   • Tooltip body strings + aria labels pulled from useTranslation("chatBot")
//     under the `liveRef.*` subtree (matches every other V3 component).
//
// Out of scope for v1 (do NOT add here):
//   • Right-click context menu — hard-deferred. No <ContextMenu> wrap,
//     no v2/ui/context-menu.tsx import.
//   • Artifact-pane deep-link — see onClick note above.
//   • "Last updated" timestamp + symbol name in tooltip — bot doesn't
//     emit these yet. Placeholder rendered when undefined.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens — match V3's MUI-free invariant.
// Plain inline styles + scoped CSS classes from ./live-ref.css.
import * as React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../v2/ui/tooltip";

export type LiveValue = boolean | number | string | null | undefined;

export type LiveRefAnim = "none" | "breathe" | "glow" | "border" | "sweep";

export interface LiveRefV3Props {
  /** PLC address — e.g. "I 34.0", "M 85.3", "Q 40.0", "S 175.8". */
  address: string;
  /** Snapshot live value at bot-generation time. Boolean for digital, number / string for analog. */
  value?: LiveValue;
  /** Animation variant. Locked to "glow" app-wide; the prop only exists for the designer's component contract. Do NOT override at call sites. */
  anim?: LiveRefAnim;
  /**
   * Open the live watcher / artifact pane. Hard-deferred to the follow-up
   * ticket MIDAS-LIVE-REF-INTERACTIONS. Default is a console.debug placeholder
   * so click is still observable in DevTools during v1 demos.
   */
  onClick?: (address: string) => void;
  /** Inline tooltip text override. When omitted, the tooltip composes its body from value + address + i18n strings. */
  title?: string;
}

const pipColor = (value: LiveValue): string => {
  if (value === false || value === "FALSE") return "var(--red)";
  if (value === true || value === "TRUE") return "var(--green)";
  if (typeof value === "number" || (typeof value === "string" && /\d/.test(value))) {
    return "var(--accent)";
  }
  return "var(--muted)"; // unknown / value attribute missing — backwards-compat with bot pre-VALUE-ATTR
};

const animClass: Record<LiveRefAnim, string> = {
  none: "",
  breathe: "lr-anim-breathe",
  glow: "lr-anim-glow",
  border: "lr-anim-border",
  sweep: "lr-anim-sweep",
};

const formatValueForTooltip = (value: LiveValue): string => {
  if (value === undefined || value === null) return "—";
  if (value === true) return "TRUE";
  if (value === false) return "FALSE";
  return String(value);
};

const defaultOnClick = (address: string): void => {
  // Placeholder per LIVE_REF_INTEGRATION_SPEC §"Click — DEFERRED".
  // Real artifact-pane wiring lives in the follow-up ticket
  // MIDAS-LIVE-REF-INTERACTIONS. We keep an observable trace so manual
  // demo / QA can confirm the chip is interactive without a real handler.
  // eslint-disable-next-line no-console
  console.debug("[LiveRefV3] click (no-op placeholder)", { address });
};

export const LiveRefV3: React.FC<LiveRefV3Props> = ({
  address,
  value,
  anim = "glow",
  onClick,
  title,
}) => {
  const { translate } = useTranslation("chatBot");

  const handleClick = React.useCallback(() => {
    if (onClick) {
      onClick(address);
    } else {
      defaultOnClick(address);
    }
  }, [address, onClick]);

  const pip = pipColor(value);
  const hasValue = value !== undefined && value !== null && value !== "";

  // Tooltip body — composed (not interpolated into a single string) so the
  // label/value rows can carry their own styling (mono + colour). The bot
  // doesn't emit "last updated" or "symbol name" yet — those rows stay out
  // for v1 to avoid showing empty/placeholder strings that look broken.
  const valueLabel = translate("liveRef.tooltip.value", {
    defaultValue: "Live PLC value:",
  }) as string;
  const addressLabel = translate("liveRef.tooltip.address", {
    defaultValue: "Address:",
  }) as string;
  const ariaChip = translate("liveRef.aria.chip", {
    defaultValue: "Live PLC reference {{address}}",
    address,
  }) as string;

  const chip = (
    <span
      className="live-ref"
      role="button"
      tabIndex={0}
      aria-label={ariaChip}
      title={title}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <span
        className="live-ref__pip"
        style={{
          background: pip,
          boxShadow: `0 0 0 2px color-mix(in oklab, ${pip} 22%, transparent)`,
        }}
        aria-hidden
      />
      <span className={`live-ref__label ${animClass[anim]}`}>
        {anim === "sweep" && <span className="live-ref__sweep" aria-hidden />}
        LIVE
      </span>
      <span className="live-ref__address">{address}</span>
    </span>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent
        side="top"
        style={{
          // The shadcn TooltipContent (v2/ui/tooltip) applies the Tailwind
          // classes `bg-popover text-popover-foreground`, which resolve
          // `--popover` / `--popover-foreground`. Those tokens are defined
          // ONLY on `.midas-v2-root` (src/index.scss) — they do NOT exist on
          // `.midas-v3-root`, so inside V3 the background resolves to nothing
          // and the tooltip renders transparent / unreadable.
          //
          // Radix 1.2.8 `Tooltip.Content` is NOT auto-portaled (the shadcn
          // wrapper never wraps it in `Tooltip.Portal`), so this content node
          // renders INLINE inside `.midas-v3-root`. That means the V3 tokens
          // below (--card / --ink / --border / --shadow / --font-mono /
          // --red / --green / --accent) DO resolve here. We give the tooltip
          // an explicit opaque surface via those tokens so it no longer
          // depends on the undefined `bg-popover`. Inline styles override the
          // Tailwind utility classes regardless of specificity. Works in both
          // `.p-claude` and `.p-machineid` palettes.
          background: "var(--card)",
          color: "var(--ink)",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow)",
          borderRadius: 6,
          padding: "8px 10px",
          fontFamily: "var(--font-sans)",
          fontSize: 12,
          lineHeight: 1.4,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div>
            <span style={{ color: "var(--muted)", marginRight: 6 }}>
              {addressLabel}
            </span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                color: "var(--ink)",
              }}
            >
              {address}
            </span>
          </div>
          {hasValue && (
            <div>
              <span style={{ color: "var(--muted)", marginRight: 6 }}>
                {valueLabel}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontWeight: 600,
                  color: pip,
                }}
              >
                {formatValueForTooltip(value)}
              </span>
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

// ─── <ValV3> companion ──────────────────────────────────────────────
//
// Renders the surrounding prose's TRUE / FALSE / numeric text with the same
// red / green / accent semantic the chip uses. Used by the markdown layer
// when the bot emits `<val>FALSE</val>` companion tags in prose around a
// chip — but ALSO usable directly from React (the simulated-chat-example
// in the handoff package uses both flows).
//
// Falls back to plain ink colour for any non-TRUE/FALSE/numeric content
// so it remains safe to wrap arbitrary text without surprising colouring.

export interface ValV3Props {
  children: React.ReactNode;
}

const valTextOf = (children: React.ReactNode): string => {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (typeof children === "boolean") return children ? "TRUE" : "FALSE";
  if (Array.isArray(children)) return children.map(valTextOf).join("");
  if (React.isValidElement(children)) {
    return valTextOf(
      (children as React.ReactElement<{ children?: React.ReactNode }>).props.children
    );
  }
  return "";
};

const valueColor = (text: string): string => {
  const t = text.trim();
  if (t === "FALSE") return "var(--red)";
  if (t === "TRUE") return "var(--green)";
  if (/\d/.test(t)) return "var(--accent)";
  return "var(--ink)";
};

export const ValV3: React.FC<ValV3Props> = ({ children }) => {
  const text = valTextOf(children).trim();
  return (
    <b
      style={{
        color: valueColor(text),
        fontFamily: "var(--font-mono)",
        fontSize: "0.96em",
        letterSpacing: "0.01em",
      }}
    >
      {children}
    </b>
  );
};
